import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Avatar, AvatarFallback, AvatarImage } from '@mdvip/shared/components/ui/avatar';
import { Button } from '@mdvip/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@mdvip/shared/components/ui/card';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import { initials } from '@mdvip/shared/lib/initials';
import { formatPhoneDisplay } from '@mdvip/shared/lib/phone';
import { useTranslation } from 'react-i18next';
import type { PracticeInfo, PracticePhysician } from '@/hooks/practice/practiceApi';

interface PhysicianCardProps {
  practice: PracticeInfo;
  /** Renders the "View Profile" action only when provided — no destination exists for it yet. */
  onViewProfile?: () => void;
}

/**
 * Avatar-fallback initials from the physician's own name parts — `practice.name` is the whole
 * display label including credentials ("David Rizzo, DO") and isn't parsed for this; `physician`
 * carries the parts directly.
 */
function physicianInitials(physician: PracticePhysician | null): string {
  return physician ? initials(physician) : '';
}

interface PhoneLineProps {
  label: string;
  value: string | null;
}

/** A dialable phone line — the underline sits on the number only, never the label. Renders nothing
 * when the practice has no number on file, rather than a dead `tel:` link. */
function PhoneLine({ label, value }: PhoneLineProps) {
  if (!value) {
    return null;
  }
  return (
    <a href={`tel:${value.replace(/\D/g, '')}`} className="block text-sm text-foreground hover:text-primary">
      <span className="text-muted-foreground">{label} </span>
      <span className="underline decoration-from-font underline-offset-2">{formatPhoneDisplay(value)}</span>
    </a>
  );
}

/**
 * The member's assigned physician, as a read-only card in the Profile rail: photo (or initials)
 * avatar, name, office and emergency numbers as dialable links, and an optional link out to a
 * physician profile view.
 */
export function PhysicianCard({ practice, onViewProfile }: PhysicianCardProps) {
  const { t } = useTranslation('members');

  return (
    <Card className="gap-4" data-slot="card-physician">
      <CardHeader>
        <CardTitle className="text-sm">{t('profile.physician.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex items-center gap-4">
          <Avatar size="lg">
            {practice.imageUrl ? <AvatarImage src={practice.imageUrl} alt="" /> : null}
            <AvatarFallback>{physicianInitials(practice.physician)}</AvatarFallback>
          </Avatar>
          <div className="flex min-w-0 flex-col gap-1">
            <div className="truncate text-base font-semibold text-foreground">
              {practice.name ?? t('profile.notOnFile')}
            </div>
            <PhoneLine label={t('profile.physician.officeLabel')} value={practice.officePhone} />
            <PhoneLine label={t('profile.physician.emergencyLabel')} value={practice.emergencyPhone} />
          </div>
        </div>
        {onViewProfile && (
          <Button
            variant="link"
            size="sm"
            onClick={onViewProfile}
            className="w-fit gap-2 px-0 text-foreground hover:text-primary hover:no-underline"
          >
            {t('profile.physician.viewProfile')}
            <Icon name="chevronRight" size="sm" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

/** One phone line's placeholder — a single skeleton bar. */
function PhoneLineSkeleton() {
  return <Skeleton className="h-4 w-32" />;
}

/** Loading placeholder — the card heading renders as real text immediately; the avatar, name and
 * phone lines beneath it render as placeholder shapes. No View Profile placeholder, matching
 * ProfileDetailsCardSkeleton's convention of not rendering an action affordance during loading. */
export function PhysicianCardSkeleton() {
  const { t } = useTranslation('members');
  return (
    <Card className="gap-4" data-slot="card-physician-skeleton" aria-busy="true">
      <CardHeader>
        <CardTitle className="text-sm">{t('profile.physician.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <Skeleton className="h-5 w-40" />
            <PhoneLineSkeleton />
            <PhoneLineSkeleton />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
